import React, { ComponentType } from 'react'
import { useIsSsr } from '../isSsr'
import { DATA_TESTID_URL_PARAM, DATA_TESTID_URL_VALUE } from '../constants'

export type WithAutomaticTestIdsType<T> = T & {
  getDataTestIdObject: (
    prefix: string,
    id?: string
  ) => { 'data-testid'?: string }
}

const useTestIdsEnabled = (): boolean => {
  const isSsr = useIsSsr()
  const searchParams = !isSsr
    ? new URLSearchParams(window.location.search)
    : undefined
  const areTestIdsEnabled =
    searchParams?.get(DATA_TESTID_URL_PARAM) === DATA_TESTID_URL_VALUE
  return areTestIdsEnabled
}

const getDataTestIdObject = (
  areDataTestIdsEnabled: boolean,
  prefix: string,
  id?: string
): { 'data-testid'?: string } =>
  areDataTestIdsEnabled && id
    ? { 'data-testid': [prefix, id, 'test-id'].filter(Boolean).join('-') }
    : {}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withAutomaticTestIds<P = any, AdditionalProps = any>(
  DecoratedComponent: ComponentType<
    WithAutomaticTestIdsType<P> & AdditionalProps
  >
) {
  const WithAutomaticTestIds = (
    props: WithAutomaticTestIdsType<P> & AdditionalProps
  ) => {
    const areTestIdsEnabled = useTestIdsEnabled()
    const additionalProps = {
      getDataTestIdObject: (
        prefix: string,
        id?: string
      ): { 'data-testid'?: string } =>
        getDataTestIdObject(areTestIdsEnabled, prefix, id)
    }

    return <DecoratedComponent {...props} {...additionalProps} />
  }

  return WithAutomaticTestIds
}
